import { ReactNode } from 'react';
import clsx from 'clsx';
import { Spinner } from '../Spinner/Spinner';

export type ButtonVariant = 'filled' | 'outline' | 'text';
export type ButtonColorScheme = 'primary' | 'secondary' | 'warning' | 'emphasis' | 'mercari';
export type ButtonSize = 'small' | 'medium' | 'large';

export type ButtonProps = {
  onClick?: () => void;
  variant?: ButtonVariant;
  colorScheme?: ButtonColorScheme;
  disabled?: boolean;
  loading?: boolean;
  size?: ButtonSize;
  isFullWidth?: boolean;
  icon?: ReactNode;
  className?: string;
  children: ReactNode;
};

const sizeClasses: Record<ButtonSize, string> = {
  small: 'h-8 px-4 py-1 h-[32px] text-x-small rounded-special-full',
  medium: 'h-10 px-4 py-2 text-medium rounded-medium',
  large: 'h-14 p-4 flex gap-2 text-medium rounded-medium',
};

const spinnerSizeClasses: Record<ButtonSize, string> = {
  small: 'w-4 h-4',
  medium: 'w-6 h-6',
  large: 'w-6 h-6',
};

const variantClasses: Record<ButtonVariant, Record<ButtonColorScheme, string>> = {
  filled: {
    primary:
      'bg-brand-primary text-neutral-white hover:bg-brand-highlight disabled:opacity-50 disabled:text-neutral-tertiary border-transparent',
    secondary:
      'bg-neutral-auxiliary text-neutral-primary hover:bg-neutral-auxiliary-tinge disabled:opacity-50 disabled:text-neutral-tertiary border-transparent',
    warning:
      'bg-semantic-red text-neutral-white hover:bg-semantic-red disabled:opacity-50 disabled:text-neutral-tertiary border-transparent',
    emphasis:
      'bg-neutral-primary text-neutral-white hover:bg-neutral-secondary disabled:opacity-50 disabled:text-neutral-tertiary border-transparent',
    mercari:
      'bg-brand-primary text-neutral-white hover:bg-brand-highlight disabled:opacity-50 disabled:text-neutral-tertiary border-transparent',
  },
  outline: {
    primary:
      'border-thin border-brand-primary text-brand-primary hover:bg-brand-tinge disabled:opacity-50 disabled:border-neutral-tertiary disabled:text-neutral-tertiary',
    secondary:
      'border-thin border-neutral-auxiliary text-neutral-primary hover:bg-neutral-auxiliary-tinge disabled:opacity-50 disabled:border-neutral-tertiary disabled:text-neutral-tertiary',
    warning:
      'border-thin border-semantic-red text-semantic-red hover:bg-semantic-red-tinge disabled:opacity-50 disabled:border-neutral-tertiary disabled:text-neutral-tertiary',
    emphasis:
      'border-thin border-neutral-primary text-neutral-primary hover:bg-neutral-auxiliary-tinge disabled:opacity-50 disabled:border-neutral-tertiary disabled:text-neutral-tertiary',
    mercari:
      'border-thin border-brand-primary text-brand-primary hover:bg-brand-tinge disabled:opacity-50 disabled:border-neutral-tertiary disabled:text-neutral-tertiary',
  },
  text: {
    primary:
      'text-brand-primary hover:bg-brand-tinge disabled:opacity-50 disabled:text-neutral-tertiary',
    secondary:
      'text-neutral-primary hover:bg-neutral-auxiliary-tinge disabled:opacity-50 disabled:text-neutral-tertiary',
    warning:
      'text-semantic-red hover:bg-semantic-red-tinge disabled:opacity-50 disabled:text-neutral-tertiary',
    emphasis:
      'text-neutral-primary hover:bg-neutral-auxiliary-tinge disabled:opacity-50 disabled:text-neutral-tertiary',
    mercari:
      'text-brand-primary hover:bg-brand-tinge disabled:opacity-50 disabled:text-neutral-tertiary',
  },
};

export function Button({
  variant = 'filled',
  colorScheme = 'primary',
  size = 'medium',
  isFullWidth = false,
  disabled = false,
  icon,
  className,
  children,
  loading = false,
  ...props
}: ButtonProps) {
  return (
    <button
      className={clsx(
        'inline-flex items-center justify-center gap-2 font-bold transition-colors duration-200 ease-in-out',
        'disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary',
        sizeClasses[size],
        variantClasses[variant][colorScheme],
        isFullWidth && 'w-full',
        className
      )}
      {...props}
    >
      {icon && <span className="flex-shrink-0">{icon}</span>}
      {loading ? <Spinner className={spinnerSizeClasses[size]} /> : <span>{children}</span>}
    </button>
  );
}
